import { Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';

const OrganizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '韩漫漫画网',
  url: 'https://www.hanmancomic.com/',
  description: '韩漫漫画网致力于漫画免费服务，拥有漫画大全和漫画完整在线观看功能。',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'contact@hanmancomic.com',
    contactType: 'customer service',
    availableLanguage: ['Chinese'],
  },
};

const WebSiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '韩漫漫画网',
  url: 'https://www.hanmancomic.com/',
  potentialAction: {
    '@type': 'SearchAction',
    'target': 'https://www.hanmancomic.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

export default function Home() {
  const hotComics = comics.filter((c) => c.rating >= 9.3).slice(0, 6);
  const latestComics = [...comics].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 8);
  const rankedComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 10);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([OrganizationSchema, WebSiteSchema]),
        }}
      />

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20epic%20fantasy%20wallpaper%2C%20vibrant%20colorful%20illustration%20with%20multiple%20characters%20in%20dynamic%20poses%2C%20warm%20orange%20and%20golden%20accents%2C%20comic%20book%20aesthetic%2C%20modern%20clean%20digital%20art%2C%20cinematic%20lighting%2C%20rich%20details&width=1600&height=900&seq=home-hero-01&orientation=landscape"
          alt="韩漫漫画网 - 免费韩漫在线阅读平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-3xl md:text-5xl font-heading text-white font-bold mb-4">
            韩漫漫画网
          </h1>
          <p className="text-base md:text-xl text-white/90 max-w-2xl mb-8 leading-relaxed">
            海量韩漫免费在线阅读，热血、恋爱、奇幻、悬疑等各类漫画一网打尽<br />
            <strong>漫画大全</strong>随心看，<strong>漫画完整在线观看</strong>尽在韩漫漫画网
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/rexue"
              className="px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              立即阅读
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-white/20 backdrop-blur-sm text-white font-medium rounded-full hover:bg-white/30 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm border border-white/30"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="w-full px-4 md:px-6 py-8 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm font-medium"
              >
                <i className={`${cat.icon} text-sm`}></i>
                <span>{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Comics */}
      <section className="w-full px-4 md:px-6 py-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold">
              <i className="ri-fire-line text-primary-500 mr-2"></i>
              热门推荐
            </h2>
            <Link to="/category/rexue" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
              查看更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {hotComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩漫免费在线阅读`}
                    title={`${comic.title} - 韩漫漫画网`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full font-medium">
                    {comic.rating}
                  </div>
                  {comic.status === '已完结' && (
                    <div className="absolute top-2 right-2 px-2 py-0.5 bg-background-900/70 text-white text-xs rounded-full">
                      已完结
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates + Ranking */}
      <section className="w-full px-4 md:px-6 py-10 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Latest Updates */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-950 font-bold">
                  <i className="ri-timer-line text-accent-500 mr-2"></i>
                  最近更新
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {latestComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 韩漫免费在线阅读`}
                        title={`${comic.title} - 韩漫漫画网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <p className="text-white text-xs">
                          更新至第{comic.chapters.length}话
                        </p>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </div>

            {/* Ranking Sidebar */}
            <div className="lg:w-80 shrink-0">
              <div className="bg-background-100 rounded-lg p-5">
                <h2 className="text-lg font-heading text-foreground-950 font-bold mb-4 flex items-center gap-2">
                  <i className="ri-trophy-line text-secondary-500"></i>
                  人气排行
                </h2>
                <div className="space-y-3">
                  {rankedComics.map((comic, index) => (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.id}`}
                      className="flex items-center gap-3 group cursor-pointer p-2 rounded-lg hover:bg-background-200 transition-colors"
                    >
                      <span
                        className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shrink-0 ${
                          index < 3
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-300 text-foreground-600'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-500">
                          {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 阅读
                        </p>
                      </div>
                      <span className="text-xs text-primary-500 font-medium whitespace-nowrap">{comic.rating}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="w-full px-4 md:px-6 py-12 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center px-6 py-8 rounded-lg bg-background-50">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                <i className="ri-book-open-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-900 mb-2">海量漫画资源</h3>
              <p className="text-sm text-foreground-600">收录数万部热门韩漫，涵盖热血、恋爱、奇幻等多种类型，<strong>漫画大全</strong>随心选。</p>
            </div>
            <div className="text-center px-6 py-8 rounded-lg bg-background-50">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                <i className="ri-smartphone-line text-2xl text-accent-500"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-900 mb-2">高清流畅阅读</h3>
              <p className="text-sm text-foreground-600">高清画质搭配流畅的阅读体验，支持手机和电脑<strong>漫画完整在线观看</strong>。</p>
            </div>
            <div className="text-center px-6 py-8 rounded-lg bg-background-50">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                <i className="ri-shield-check-line text-2xl text-secondary-500"></i>
              </div>
              <h3 className="text-base font-semibold text-foreground-900 mb-2">完全免费</h3>
              <p className="text-sm text-foreground-600"><strong>韩漫画免费网站</strong>，所有漫画无需付费即可畅享阅读，每日更新不停歇。</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full px-4 md:px-6 py-16 bg-primary-500">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading text-white font-bold mb-4">
            开始你的漫画之旅
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto text-sm md:text-base">
            加入韩漫漫画网，畅享海量免费韩漫资源。每日更新，<strong>漫画完整在线观看</strong>，精彩不容错过！
          </p>
          <Link
            to="/category/rexue"
            className="inline-flex px-8 py-3 bg-white text-primary-600 font-semibold rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
          >
            立即探索漫画大全
          </Link>
        </div>
      </section>
    </>
  );
}